import React, { useEffect } from "react";

import { useMsal } from "@azure/msal-react";
import { useNavigate } from "@tanstack/react-router";

import { DEFAULT_PROTECTED_ROUTE } from "../constants/auth";
import { setAccessTokenCookie } from "../utils/cookies";
import { consumeFallbackTarget } from "../utils/msalTabSession";
import { PageLoader } from "./PageLoader";

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();

  useEffect(() => {
    // StrictMode runs this twice in dev; only the live run may navigate, or the
    // second would replace the saved route with the default.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const goToSignedInRoute = () => {
      if (cancelled) return;
      const target = consumeFallbackTarget();
      if (target) navigate({ href: target, replace: true });
      else navigate({ to: DEFAULT_PROTECTED_ROUTE, replace: true });
    };

    // The sign-in failed, so a saved route must not outlive it.
    const goToLanding = () => {
      if (cancelled) return;
      consumeFallbackTarget();
      navigate({ to: "/", replace: true });
    };

    const handleAuthCallback = async () => {
      try {
        // Handle the redirect promise - this completes the login flow
        const response = await instance.handleRedirectPromise();

        if (response?.account) {
          // Set active account
          instance.setActiveAccount(response.account);

          // Set access token if available
          if (response.accessToken) {
            setAccessTokenCookie(response.accessToken);
          }

          goToSignedInRoute();
        } else {
          // No response means redirect was already handled or no redirect was pending
          // Wait a bit and check if user is authenticated
          timer = setTimeout(() => {
            const accounts = instance.getAllAccounts();
            if (accounts.length > 0) {
              instance.setActiveAccount(accounts[0]);
              goToSignedInRoute();
            } else {
              // If no accounts, redirect to landing page
              goToLanding();
            }
          }, 500);
        }
      } catch (error) {
        console.error("Error handling auth callback:", error);
        // On error, redirect to landing page
        goToLanding();
      }
    };

    handleAuthCallback();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [navigate, instance]);

  return <PageLoader message="Signing in..." />;
};
